import type { ComponentType, ReactNode } from 'react';

import type { ButtonVariant } from '@/ui/components/Button';
import type { Hue } from '@/ui/components/hue';
import type { IconName } from '@/ui/components/iconName';

/**
 * THE SHAPES THE SHELL ACCEPTS — docs/ARCHITECTURE.md §3 ("ui/ — presentation only: props
 * in, JSX out"), ADR-0003, ADR-0014, ADR-0020.
 *
 * ═══ WHY THESE TYPES EXIST AT ALL, GIVEN `Command` AND `ViewDescriptor` ALREADY DO ═══
 *
 * They are NOT a duplicate of `src/features/types.ts`, and the difference is not stylistic.
 * Gate 2 forbids `src/ui/**` from importing `@/features/**` — including `import type`. That
 * is not an inconvenience to route around: `Command` carries `labelKey`, `isAvailable(ctx)`
 * and `run(ctx, doc)`, and every one of those is something the shell must not be able to do.
 * A toolbar that held a `Command` would need a `CommandContext` — services, a translator, a
 * message catalog — to decide whether to draw a button, and at that point `ui/` is the
 * application rather than its surface.
 *
 * So the boundary is a projection, performed once, in `src/app/`:
 *
 *   Command       →  ToolbarItem   labelKey resolved to `label`, isAvailable(ctx) collapsed
 *                                  to `available`, isEnabled(doc) to `disabled`, run() closed
 *                                  over into `onInvoke`, `shortcut` rendered for THIS platform
 *                                  into `shortcutHint` (⌘ vs Ctrl is not a ui/ concern).
 *   ViewDescriptor → ShellView<P>  labelKey resolved, `select(doc)` already applied and the
 *                                  result packed into `props`, `load` passed through untouched
 *                                  so the laziness survives, `maxBytes` collapsed to either
 *                                  `unavailable` content or nothing.
 *
 * Everything above is a plain value by the time it reaches here. A shell component cannot
 * translate, cannot reach a service, and cannot decide policy, because none of those things
 * are in its props — which is the property ADR-0003 is trying to buy, expressed as types
 * rather than as a review habit.
 */

// ── the toolbar ─────────────────────────────────────────────────────────────────────────

/**
 * Mirrors `CommandGroup` in `src/features/types.ts` by construction, not by import. The shell
 * uses it for exactly one thing: a hairline separator appears wherever the group changes
 * between two adjacent buttons (docs/DESIGN.md §6.2). It is never compared to a literal here.
 */
export type ToolbarGroup = 'transform' | 'clipboard' | 'document' | 'view';

/**
 * Every optional below is written `?: T | undefined` rather than `?: T`, and that is not
 * decoration. `exactOptionalPropertyTypes` is on, so `?: T` means "absent or a T" and makes
 * `shortcutHint={item.shortcutHint}` — forwarding a value that may not be there — a type
 * error rather than a no-op. These fields exist to be forwarded, so they say so.
 */
export interface ToolbarItem {
  readonly id: string;
  /** Already localised. `ui/**` does not translate (ADR-0003, ADR-0018). */
  readonly label: string;
  readonly icon: IconName;
  readonly group: ToolbarGroup;
  /** Defaults to `'tinted'`; `Command.variant` maps onto it 1:1. */
  readonly variant?: ButtonVariant | undefined;
  /**
   * The hue family of a tinted control — `Command.hue`, passed through. Omitted means the
   * neutral `peri`. Mirrors the command field by construction, like `ToolbarGroup` does.
   */
  readonly hue?: Hue | undefined;
  /**
   * A SHORTER visible word below 960px — "Example" where the label is "Load example"
   * (ADR-0101 §7.1). Already localised, like `label`. Visual only: the accessible name stays
   * `label` at every width, so what a screen reader hears never depends on the window.
   */
  readonly compactLabel?: string | undefined;
  /**
   * A count drawn as a lemon pill after the label — how many problems "Fix all" would repair.
   * Rendered only above zero, and `aria-hidden`: the status line already announces the number,
   * and a name that changed on every keystroke would re-announce the button on each one.
   */
  readonly badge?: number | undefined;
  /**
   * The shortcut printed ON the control at rest — `'⌘⏎'`, `'Ctrl+Enter'`. Already rendered
   * for this platform by the caller: the shell does not know which machine it is running on.
   */
  readonly shortcutHint?: string | undefined;
  /** Localised `title`, usually `a11y.titleWithShortcut`. */
  readonly title?: string | undefined;
  /**
   * Localised accessible name, usually `a11y.actionWithShortcut`. Falls back to `label`, so
   * a button can never end up nameless.
   */
  readonly ariaLabel?: string | undefined;
  /**
   * `false` ⇒ NOT RENDERED AT ALL — the Paste button in Firefox, where the clipboard cannot
   * be read (`Command.isAvailable`). Absent means available: an item that forgot to say is
   * shown, because a silently missing action is worse than a superfluous one.
   */
  readonly available?: boolean | undefined;
  /** `true` ⇒ rendered, disabled (`Command.isEnabled === false`). */
  readonly disabled?: boolean | undefined;
  /** `'end'` places the item after the flexible gap — "Load example" (docs/DESIGN.md §6.2). */
  readonly align?: 'start' | 'end' | undefined;
  readonly onInvoke: () => void;
}

// ── the views ───────────────────────────────────────────────────────────────────────────

/** What `TabBar` needs. A projection of `ViewDescriptor` minus `select` and `load`. */
export interface ViewTab {
  readonly id: string;
  /** Already localised — `ViewDescriptor.labelKey` resolved. */
  readonly label: string;
  readonly icon: IconName;
  readonly beta?: boolean | undefined;
  /** `true` ⇒ shown, not selectable (a view whose `maxBytes` the document exceeds). */
  readonly disabled?: boolean | undefined;
}

/**
 * What `ViewHost` needs: the ACTIVE view only.
 *
 * `props` is the already-computed `select(doc)` output plus the `FeatureApi` — one opaque
 * bag, generic so that the shell never has to name `FeatureApi`. The host spreads it into
 * the lazily loaded panel and looks at none of it.
 */
export interface ShellView<P extends object> {
  readonly id: string;
  /** The lazy module. Passed through untouched: this is what keeps a heavy tab free. */
  readonly load: () => Promise<{ default: ComponentType<P> }>;
  readonly props: P;
  /**
   * Rendered INSTEAD of the panel, and `load` is then never called — the declarative
   * `maxBytes` escape of ADR-0020 §4, and the reason a tab can refuse a 30 MB document
   * without freezing the tab to find out.
   */
  readonly unavailable?: ReactNode;
}

// ── the ids that tie a tab to its panel ─────────────────────────────────────────────────

/**
 * `TabBar` and `ViewHost` are siblings, not parent and child, so `aria-controls` and
 * `aria-labelledby` have to agree on a DOM id across two files. Deriving both from the view
 * id in one place is the only way that agreement survives a rename.
 */
export const tabDomId = (viewId: string): string => `view-tab-${viewId}`;
export const panelDomId = (viewId: string): string => `view-panel-${viewId}`;
